import { useState } from 'react';
import styled from 'styled-components';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { SupplierSummary } from './types';

export const SupplierCard = ({ supplier }: { supplier: SupplierSummary }) => {
    const [isOpen, setIsOpen] = useState(false);
    const totalUnits = supplier.products.reduce((sum, p) => sum + p.totalQuantity, 0);

    return (
        <Card>
            <CardHeader onClick={() => setIsOpen(!isOpen)}>
                <CardHeaderContent>
                    <SupplierName>{supplier.supplierName}</SupplierName>
                    <SupplierStats>
                        {supplier.products.length} produit{supplier.products.length > 1 ? 's' : ''} • {totalUnits}{' '}
                        unités
                    </SupplierStats>
                </CardHeaderContent>
                <IconWrapper>{isOpen ? <FaChevronUp /> : <FaChevronDown />}</IconWrapper>
            </CardHeader>

            {isOpen && (
                <CardContent>
                    <ProductsTable>
                        <thead>
                            <tr>
                                <TableHeader>Produit</TableHeader>
                                <TableHeader align="right">Qté</TableHeader>
                            </tr>
                        </thead>
                        <tbody>
                            {supplier.products.map((product) => (
                                <ProductRow key={product.productId}>
                                    <ProductCell>
                                        {product.imageUrl && <ProductImage src={product.imageUrl} alt="" />}
                                        <ProductName>{product.productName}</ProductName>
                                    </ProductCell>
                                    <QuantityCell>{product.totalQuantity}</QuantityCell>
                                </ProductRow>
                            ))}
                        </tbody>
                    </ProductsTable>
                </CardContent>
            )}
        </Card>
    );
};

const Card = styled.div`
    background-color: #ffffff;
    border: 1px solid ${({ theme }) => theme.darkBorderColor || '#e0e0e0'};
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    transition: box-shadow 0.2s;
    height: fit-content;

    &:hover {
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    }
`;

const CardHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px;
    background-color: #ffffff;
    cursor: pointer;
    user-select: none;
    transition: background-color 0.2s;

    &:hover {
        background-color: #f8f9fa;
    }
`;

const CardHeaderContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const IconWrapper = styled.div`
    color: ${({ theme }) => theme.textColor || '#333'}99;
    font-size: 14px;
`;

const CardContent = styled.div`
    border-top: 1px solid ${({ theme }) => theme.darkBorderColor || '#e0e0e0'};
    max-height: 400px;
    overflow-y: auto;
    background-color: #ffffff;
`;

const SupplierName = styled.h3`
    margin: 0;
    font-size: 16px;
    font-weight: bold;
    color: ${({ theme }) => theme.textColor || '#333'};
`;

const SupplierStats = styled.span`
    font-size: 13px;
    color: #666;
`;

const ProductsTable = styled.table`
    width: 100%;
    border-collapse: collapse;
`;

const TableHeader = styled.th<{ align?: string }>`
    padding: 10px 15px;
    text-align: ${({ align }) => align || 'left'};
    font-weight: 600;
    font-size: 12px;
    color: #666;
    border-bottom: 1px solid ${({ theme }) => theme.darkBorderColor || '#e0e0e0'};
    background-color: #f8f9fa;
    position: sticky;
    top: 0;
    z-index: 1;
`;

const ProductRow = styled.tr`
    &:hover {
        background-color: #f8f9fa;
    }

    &:not(:last-child) td {
        border-bottom: 1px solid #f0f0f0;
    }
`;

const ProductCell = styled.td`
    padding: 8px 15px;
    display: flex;
    align-items: center;
    gap: 10px;
    color: ${({ theme }) => theme.textColor || '#333'};
`;

const ProductImage = styled.img`
    width: 32px;
    height: 32px;
    object-fit: cover;
    border-radius: 4px;
    border: 1px solid #e0e0e0;
`;

const ProductName = styled.span`
    font-size: 13px;
    white-space: normal;
    word-break: break-word;
`;

const QuantityCell = styled.td`
    padding: 8px 15px;
    text-align: right;
    font-weight: 600;
    font-size: 14px;
    color: ${({ theme }) => theme.ctaPrimaryColor || '#00b54b'};
`;
